<script setup>
import {
  cloneDeep, isEmpty,
} from 'lodash';
import {
  reactive, ref, onMounted, onUnmounted, computed, inject,
} from 'vue';
import axios from 'axios';
import { redirectTo } from '@/libs/cifra/urls';
import { getModelJsonErrors, modelErrorsToHtmlText } from '../../../../libs/cifra/model_errors';
import TematicasForm from './TematicasForm.vue';
import CursosForm from './CursosForm.vue';
import ActividadesForm from './ActividadesForm.vue';

const toast = inject('toast');
const $I18n = inject('I18n');

const props = defineProps({
  model: { type: Object, required: true },
  cancelarUrl: { type: String, default: null },
});

const situacionAprendizaje = reactive({ ...cloneDeep(props.model) }) || {
  titulo: null,
  fecha_inicio: null,
  fecha_fin: null,
  horas: 0,
  tematicas: [],
  objetivos: [],
  saberes: [],
  medidas_ordinarias: null,
  medidas_especificas: null,
  recursos: null,
  tecnicas: null,
  instrumentos: null,
  resumen: null,
  aprendizajes: null,
  cursos: [],
  actividades: [],
  foto: null,
};

const guardando = ref(false);
const mainForm = ref(null);
const selectorImagen = ref(null);

const identificacion = ref(null);
const concrecionCurricular = ref(null);
const secuenciaDidactica = ref(null);
const diversidad = ref(null);
const evaluacion = ref(null);

const errores = ref({});
const pasoActual = ref(0);

const pasos = [identificacion, concrecionCurricular, secuenciaDidactica, diversidad, evaluacion];

const progresoStyle = ref('');
const isPrimerPaso = computed(() => pasoActual?.value === 0);
const isUltimopaso = computed(() => pasoActual?.value === pasos.length - 1);
const puedeGuardar = computed(() => isUltimopaso.value || situacionAprendizaje.id !== null);

let cuerpoObserver = null;

function calcularProgreso() {
  if (isUltimopaso.value || pasoActual.value > pasos.length) {
    return 'width: 100%';
  }
  const first = pasos[0]?.value;
  const actual = pasos[pasoActual.value]?.value;

  if (first && actual) {
    const delta = (actual.getBoundingClientRect().right - 1) - first.getBoundingClientRect().left;
    return `width: ${delta}px;`;
  }
  return 'width: 0%';
}

const progressBarStyle = computed(() => calcularProgreso());

function avanzar() {
  const paso = pasoActual.value + 1;
  if (paso <= pasos.length - 1) {
    pasoActual.value += 1;
  }
}

function retroceder() {
  const paso = pasoActual.value - 1;
  if (paso >= 0) {
    pasoActual.value -= 1;
  }
}

function observarCuerpo(entries) {
  if (!mainForm.value) return;

  const entry = entries[0];
  const rect = entry.target.getBoundingClientRect();

  const first = pasos[0].value.getBoundingClientRect();
  const last = pasos[pasos.length - 1].value.getBoundingClientRect();
  const left = first.left - rect.left;
  progresoStyle.value = `left: ${left}px; width: ${last.right - first.left}px;`;
}

function isActivo(paso) {
  return paso === pasos[pasoActual.value].value;
}

function isCompletado(paso) {
  return pasos.findIndex((p) => p.value === paso) < pasoActual.value;
}

function fotoModificada() {
  const foto = selectorImagen.value.getImage();

  if (foto) {
    situacionAprendizaje.foto = foto;
  } else if (situacionAprendizaje.foto_expiring_url) {
    situacionAprendizaje.delete_foto = true;
  }
}

function establecerPasoActual(paso) {
  const indexSeleccionado = pasos.findIndex((element) => element.value === paso);

  if (indexSeleccionado >= 0 || indexSeleccionado <= pasos.length - 1) {
    pasoActual.value = indexSeleccionado;
  }
}

function addTematicas(tematicas) {
  situacionAprendizaje.tematicas = tematicas.filter((tematica) => !isEmpty(tematica));
}

function addCursos(cursos) {
  situacionAprendizaje.cursos = cursos.filter((curso) => !isEmpty(curso));
}

function prepararSituacionAprendizajeData() {
  const nueva = cloneDeep(situacionAprendizaje);

  nueva.cursos_attributes = nueva.cursos.map((curso) => {
    curso.competencias_clave_attributes = curso.competencias_clave.map((cc) => {
      cc.descriptores_attributes = cc.descriptores;
      return cc;
    });
    curso.asignaturas_attributes = curso.asignaturas.map((asg) => {
      asg.competencias_especificas_attributes = asg.competencias_especificas.map((ce) => {
        ce.criterios_attributes = ce.criterios;

        delete ce.criterios;

        return ce;
      });

      delete asg.competencias_especificas;

      return asg;
    });

    delete curso.curso;
    delete curso.competencias_clave;
    delete curso.asignaturas;

    return curso;
  });

  delete nueva.cursos;

  nueva.actividades_attributes = nueva.actividades.map((act) => {
    act.archivos_attributes = act.archivos;

    delete act.archivos;

    return act;
  });

  delete nueva.actividades;
  delete nueva.cursos;

  return nueva;
}

function nombreParaCurso(curso) {
  return curso.curso.nombre_completo
    || `${curso.curso.nombre} / ${curso.curso.ensennanza.tipo_ensennanza} / ${curso.curso.anno_lectivo.nombre}`;
}

function guardar() {
  guardando.value = true;

  let url = '/lomloe/situaciones_aprendizaje';
  let method = 'post';
  let headers = {};

  if (situacionAprendizaje.foto) {
    headers = { 'Content-Type': 'multipart/form-data' };
  }

  if (situacionAprendizaje.id) {
    url = `${url}/${situacionAprendizaje.id}`;
    method = 'patch';
  }

  const nueva = prepararSituacionAprendizajeData();

  axios({
    url,
    method,
    data: { lomloe_situacion_aprendizaje: nueva },
    headers,
  }).then((result) => {
    const modelo = result.data;
    errores.value = {};

    redirectTo(`/lomloe/situaciones_aprendizaje/${modelo.id}/edit`, { refresh: true });
  }).catch((e) => {
    errores.value = getModelJsonErrors(e.response.data);
    toast.error($I18n.t('Error_guardar_campos'));
  }).then(() => {
    guardando.value = false;
  });
}

onMounted(() => {
  if (cuerpoObserver) {
    cuerpoObserver.unobserve();
  }

  cuerpoObserver = new ResizeObserver(observarCuerpo);
  cuerpoObserver.observe(mainForm.value);
});

onUnmounted(() => {
  cuerpoObserver.disconnect();
});
</script>

<template>
  <form ref="mainForm" class="main-form" @submit.prevent="guardar">
    <InputCsrf />
    <div class="nav-wrapper">
      <div class="progreso" :style="progresoStyle">
        <div
          class="progress-bar"
          role="progressbar"
          :style="progressBarStyle"
        >
        </div>
      </div>
      <ul class="nav nav-pills m-b-xs">
        <li
          ref="identificacion"
          class="nav-item"
          :class="{
            active: isActivo(identificacion),
            completed: isCompletado(identificacion)
          }"
        >
          <a href="#" @click="establecerPasoActual(identificacion)">
            {{ $I18n.t('identificacion') }}
          </a>
        </li>
        <li
          ref="concrecionCurricular"
          class="nav-item"
          :class="{
            active: isActivo(concrecionCurricular),
            completed: isCompletado(concrecionCurricular)
          }"
        >
          <a href="#" @click="establecerPasoActual(concrecionCurricular)">
            {{ $I18n.t('concrecion_curricular') }}
          </a>
        </li>
        <li
          ref="secuenciaDidactica"
          class="nav-item"
          :class="{
            active: isActivo(secuenciaDidactica),
            completed: isCompletado(secuenciaDidactica)
          }"
        >
          <a href="#" @click="establecerPasoActual(secuenciaDidactica)">
            {{ $I18n.t('secuencia_didactica') }}
          </a>
        </li>
        <li
          ref="diversidad"
          class="nav-item"
          :class="{
            active: isActivo(diversidad),
            completed: isCompletado(diversidad)
          }"
        >
          <a href="#" @click="establecerPasoActual(diversidad)">
            {{ $I18n.t('Diversidad') }}
          </a>
        </li>
        <li
          ref="evaluacion"
          class="nav-item"
          :class="{
            active: isActivo(evaluacion),
            completed: isCompletado(evaluacion)
          }"
        >
          <a href="#" @click="establecerPasoActual(evaluacion)">
            {{ $I18n.t('Evaluacion') }}
          </a>
        </li>
      </ul>
    </div>
    <transition enter-active-class="animated fadeIn">
      <div v-show="isActivo(identificacion)" class="identificacion">
        <SelectorImagen
          ref="selectorImagen"
          :can-save="false"
          :src="situacionAprendizaje.foto_expiring_url"
          @modified="fotoModificada"
        >
        </SelectorImagen>
        <input-base
          ref="inputTitulo"
          v-model="situacionAprendizaje.titulo"
          :error="errores.titulo"
          :label-key="$I18n.t('activerecord.attributes.lomloe/situacion_aprendizaje.titulo')"
          :required="true"
        />
        <div v-if="!isEmpty(situacionAprendizaje.cursos)" class="form-group">
          <div
            v-for="infoCurso in situacionAprendizaje.cursos"
            :key="infoCurso.curso.id"
            class="cursos"
          >
            <div class="panel panel-default">
              <div class="panel-heading clearfix">
                <div class="panel-title">
                  {{ nombreParaCurso(infoCurso) }}
                </div>
              </div>
              <div class="panel-body">
                <div class="asignaturas">
                  <ul
                    v-for="infoAsignatura in infoCurso.asignaturas"
                    :key="infoAsignatura.asignatura.id"
                    class="asignaturas"
                  >
                    <li>
                      {{ infoAsignatura.asignatura.nombre }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="fecha-hora">
          <div class="fecha-inicio form-group">
            <input-fecha-hora
              v-model="situacionAprendizaje.fecha_inicio"
              :label="$I18n.t('activerecord.attributes.lomloe/situacion_aprendizaje.fecha_inicio')"
              :error="errores.fecha_inicio"
              :required="true"
            />
          </div>
          <div class="fecha-fin form-group">
            <input-fecha-hora
              v-model="situacionAprendizaje.fecha_fin"
              :error="errores.fecha_fin"
              :label="$I18n.t('activerecord.attributes.lomloe/situacion_aprendizaje.fecha_fin')"
            />
          </div>
          <div class="horas form-group">
            <label for="" class="control-label">
              {{ $I18n.t('activerecord.attributes.lomloe/situacion_aprendizaje.horas') }}
            </label>
            <selector-numerico
              v-model.number="situacionAprendizaje.horas"
              class="input-3-digitos unidades"
              size="sm"
              :min="0"
              :max="999"
            >
            </selector-numerico>
          </div>
        </div>
        <TematicasForm
          :model-value="situacionAprendizaje.tematicas"
          :error="errores.tematicas"
          @update:model-value="addTematicas($event)"
        />
      </div>
    </transition>
    <transition enter-active-class="animated fadeIn">
      <div v-show="isActivo(concrecionCurricular)" class="concrecion-curricular">
        <CursosForm
          :model-value="situacionAprendizaje.cursos"
          :error="errores"
          @update:model-value="addCursos($event)"
        />
      </div>
    </transition>
    <transition enter-active-class="animated fadeIn">
      <div v-show="isActivo(secuenciaDidactica)" class="secuencia-didactica">
        <ActividadesForm
          :model-value="situacionAprendizaje.actividades"
          :error="errores"
          @update:model-value="situacionAprendizaje.actividades = $event"
        >
        </ActividadesForm>
      </div>
    </transition>
    <transition enter-active-class="animated fadeIn">
      <div v-show="isActivo(diversidad)" class="diversidad">
        <div class="form-group">
          <label class="control-label">
            {{ $I18n.t('activerecord.attributes.lomloe/situacion_aprendizaje.medidas_ordinarias') }}
          </label>
          <textarea
            v-model="situacionAprendizaje.medidas_ordinarias"
            class="form-control"
            rows="4"
          ></textarea>
        </div>
        <div class="form-group">
          <label class="control-label">
            {{
              $I18n.t('activerecord.attributes.lomloe/situacion_aprendizaje.medidas_especificas')
            }}
          </label>
          <textarea
            v-model="situacionAprendizaje.medidas_especificas"
            class="form-control"
            rows="4"
          ></textarea>
        </div>
      </div>
    </transition>
    <transition enter-active-class="animated fadeIn">
      <div v-show="isActivo(evaluacion)" class="evaluacion">
        <div class="fila-recursos-evaluacion">
          <div class="form-group recursos">
            <label class="control-label">
              {{
                $I18n.t('activerecord.attributes.lomloe/situacion_aprendizaje.recursos')
              }}
            </label>
            <textarea
              v-model="situacionAprendizaje.recursos"
              class="form-control"
              rows="4"
            ></textarea>
          </div>
          <div class="form-group tecnicas">
            <label class="control-label">
              {{
                $I18n.t('activerecord.attributes.lomloe/situacion_aprendizaje.tecnicas')
              }}
            </label>
            <textarea
              v-model="situacionAprendizaje.tecnicas"
              class="form-control"
              rows="4"
            ></textarea>
          </div>
          <div class="form-group instrumentos">
            <label class="control-label">
              {{
                $I18n.t('activerecord.attributes.lomloe/situacion_aprendizaje.instrumentos')
              }}
            </label>
            <textarea
              v-model="situacionAprendizaje.instrumentos"
              class="form-control"
              rows="4"
            ></textarea>
          </div>
        </div>
        <div class="form-group resumen">
          <label class="control-label">
            {{ $I18n.t('activerecord.attributes.lomloe/situacion_aprendizaje.resumen') }}
          </label>
          <textarea
            v-model="situacionAprendizaje.resumen"
            class="form-control"
            rows="4"
          ></textarea>
        </div>
        <div class="form-group resumen" :class="{ 'has-error': errores.aprendizajes }">
          <label class="control-label">
            <abbr title="requerido">*</abbr>
            {{ $I18n.t('activerecord.attributes.lomloe/situacion_aprendizaje.aprendizajes') }}
          </label>
          <textarea
            v-model="situacionAprendizaje.aprendizajes"
            class="form-control"
            rows="4"
          ></textarea>
          <p v-if="errores.aprendizajes" class="text-danger">
            {{ modelErrorsToHtmlText(errores.aprendizajes) }}
          </p>
        </div>
      </div>
    </transition>
    <div class="botones">
      <div class="form-actions m-t-lg">
        <div>
          <button
            v-if="!isPrimerPaso"
            class="btn btn-primary pull-left"
            type="button"
            @click="retroceder"
          >
            <i class="fa fa-angle-left"></i> {{ $I18n.t('Anterior') }}
          </button>
        </div>
        <div>
          <a v-if="cancelarUrl" class="btn btn-default" :href="cancelarUrl">
            {{ $I18n.t('Cancelar') }}
          </a>
          <button
            v-if="!isUltimopaso"
            class="btn btn-primary"
            type="button"
            @click="avanzar"
          >
            {{ $I18n.t('Siguiente') }} <i class="fa fa-angle-right"></i>
          </button>
          <boton-guardar v-if="puedeGuardar" :submitting="guardando"></boton-guardar>
        </div>
      </div>
    </div>
  </form>
</template>

<style lang="scss" scoped>
  @import '../../../../../assets/stylesheets/cifra/vars/main.scss';

  $margen-borde-seleccion: 3px;

  .main-form {
    min-height: 200px;
  }

  .progreso {
    position: relative;
    top: 2em;
    height: 5px;
    margin: 0;
    box-shadow: none;
    background-color: #c2c2c2;
    .progress-bar {
      background-color: $primary;
    }
  }

  .nav-pills {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-bottom: 2em;

    > li {
      &.nav-item {
        margin-top: $margen-borde-seleccion * 2;
      }
      a {
        font-size: 0.9em;
      }
    }

    .active {
      background-color: $primary;
      &.nav-item {
        opacity: 1;
      }

      a {
        font-weight: bold;
      }
    }

    .completed {
      &.nav-item {
        opacity: 1;
      }

      a {
        font-weight: bold;
        background-color: $primary;
      }
    }
  }
  .fecha-hora, .fila-recursos-evaluacion {
    display: grid;
    grid-template-columns: auto auto auto;
    column-gap: 10px;
    padding-bottom: 1em;
    align-items: start;
  }

  textarea {
    border-radius: 10px;
  }

  abbr {
    border: 0;
    text-decoration: none;
    color: #fe1d12;
  }
</style>
